/**
 * Ultrasound Baseline Hook
 * 
 * Custom hook for managing ultrasound baseline data including loading,
 * error handling, and automatic data fetching on parameter changes.
 */

import { useState, useEffect, useCallback } from 'react';
import { UltsBaseline, UltrasoundError } from '../services/equipment/types';
import { ultrasoundService } from '../services/equipment/ultrasound-service';

interface UseUltsBaselineOptions {
  /** Site identifier */
  site?: string;
  /** Asset identifier */
  asset?: string;
  /** Position identifier (may include equipment prefix) */
  position?: string;
}

interface UseUltsBaselineReturn {
  /** Baseline data */
  data: UltsBaseline | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Ultrasound-specific error object for detailed error handling */
  ultrasoundError: UltrasoundError | null;
  /** Fetch baseline data function */
  fetchBaseline: (site: string, asset: string, position: string) => Promise<void>;
  /** Retry the last failed request */
  retry: () => Promise<void>;
  /** Clear data and reset state */
  clear: () => void;
}

/**
 * Hook for managing ultrasound baseline data
 */
export const useUltsBaseline = (
  options: UseUltsBaselineOptions
): UseUltsBaselineReturn => {
  const {
    site,
    asset,
    position
  } = options;

  const [data, setData] = useState<UltsBaseline | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ultrasoundError, setUltrasoundError] = useState<UltrasoundError | null>(null);
  const [currentSite, setCurrentSite] = useState<string | null>(null);
  const [currentAsset, setCurrentAsset] = useState<string | null>(null);
  const [currentPosition, setCurrentPosition] = useState<string | null>(null);

  /**
   * Fetch ultrasound baseline data
   */
  const fetchBaseline = useCallback(async (
    targetSite: string,
    targetAsset: string,
    targetPosition: string
  ) => {
    if (!targetSite || !targetAsset || !targetPosition) return;

    setIsLoading(true);
    setError(null);
    setUltrasoundError(null);

    try {
      const baselineData = await ultrasoundService.getBaseline(
        targetSite,
        targetAsset,
        targetPosition
      );
      console.log(`[use-ults-baseline.tsx] baseline data: ${baselineData}`);
      setData(baselineData);
      setCurrentSite(targetSite);
      setCurrentAsset(targetAsset);
      setCurrentPosition(targetPosition);
    } catch (err) {
      if (err instanceof UltrasoundError) {
        setUltrasoundError(err);
        setError(err.message);
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch ultrasound baseline data';
        setError(errorMessage);
        setUltrasoundError(null);
      }
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Retry the last failed request
   */
  const retry = useCallback(async () => {
    if (!currentSite || !currentAsset || !currentPosition) return;
    await fetchBaseline(currentSite, currentAsset, currentPosition);
  }, [currentSite, currentAsset, currentPosition, fetchBaseline]);

  /**
   * Clear data and reset state
   */
  const clear = useCallback(() => {
    setData(null);
    setError(null);
    setUltrasoundError(null);
    setCurrentSite(null);
    setCurrentAsset(null);
    setCurrentPosition(null);
  }, []);

  // Auto-fetch when parameters change - always fetch when valid parameters are provided
  useEffect(() => {
    // Always fetch data when all parameters are provided and any parameter has changed
    if (site && asset && position) {
      if (site !== currentSite || asset !== currentAsset || position !== currentPosition) {
        fetchBaseline(site, asset, position);
      }
    }
  }, [site, asset, position, currentSite, currentAsset, currentPosition, fetchBaseline]);

  // Clear data when parameters are removed
  useEffect(() => {
    if (
      (!site && currentSite) ||
      (!asset && currentAsset) ||
      (!position && currentPosition)
    ) {
      clear();
    }
  }, [site, currentSite, asset, currentAsset, position, currentPosition, clear]);

  return {
    data,
    isLoading,
    error,
    ultrasoundError,
    fetchBaseline,
    retry,
    clear
  };
};